import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, MessageCircle, ChevronDown, ChevronUp, Quote, CheckCircle, Mail } from 'lucide-react';

const WA_ALUMNI_URL = 'https://wa.me/919246887272?text=Hi%2C%20I%20am%20an%20alumnus%20of%20Sri%20Aurobindo%20Montessori%20and%20would%20like%20to%20share%20my%20story';
const EMAIL_ALUMNI  = 'mailto:sriaurobindo60@gmail.com?subject=Alumni%20Story%20%E2%80%94%20Sri%20Aurobindo%20Montessori';

const achievements = [
  { label: 'Gymnastics', note: 'Gold, Silver, Bronze — District, State & National level' },
  { label: 'Karate', note: 'State championship medals' },
  { label: 'Academic foundation', note: 'Strong across all subjects' },
  { label: '8 years', note: 'Completed the full Montessori programme, 2017–2025' },
];

const proofPoints = [
  { text: 'They transition into mainstream schools smoothly and are frequently among the toppers.' },
  { text: 'They speak fearlessly — the stage fright never had a chance to form.' },
  { text: 'They lead naturally, help instinctively, and manage themselves without being managed.' },
  { text: 'They achieve and stay balanced — growth on the inside matching success on the outside.' },
];

export default function Alumni() {
  const [aadhyaExpanded, setAadhyaExpanded] = useState(false);

  return (
    <div className="pt-16">
      <Helmet>
        <title>Alumni | Where Aurobindo Children Go — Sri Aurobindo Montessori, Hyderabad</title>
        <meta
          name="description"
          content="1200+ children have grown through our Montessori school in Hyderabad since 2000 — pilots, doctors, lawyers, entrepreneurs, sportspersons. The foundation stays for life."
        />
      </Helmet>

      {/* ── BANNER ── */}
      <div className="bg-[#1E3A8A] py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300 mb-4">Alumni</p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight"
            style={{ fontFamily: 'Nunito, sans-serif' }}
            data-testid="alumni-h1"
          >
            The foundation stays.<br className="hidden sm:block" /> The paths go everywhere.
          </h1>
          <p className="text-blue-200 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            A Montessori foundation isn't a head start in one subject. It's a way of being — deciding, doing, concentrating, communicating, caring — that travels into every field a child later chooses.
          </p>
        </div>
      </div>

      {/* ── 1200+ CHILDREN, COUNTLESS DIRECTIONS ── */}
      <section className="py-20 bg-white" data-testid="alumni-stats-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            {/* Left: text */}
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">Our reach</p>
              <h2
                className="text-2xl sm:text-3xl font-extrabold text-[#1E3A8A] mb-6 leading-snug"
                style={{ fontFamily: 'Nunito, sans-serif' }}
              >
                1200+ children, countless directions
              </h2>
              <p className="text-slate-600 leading-relaxed text-base">
                Since 2000, more than 1,200 children have grown through the House of Children. Today they are pilots, doctors, lawyers, entrepreneurs and cricketers — and, just as importantly, balanced adults who live life in many dimensions, not one.
              </p>
              <p className="text-slate-600 leading-relaxed text-base mt-4">
                Our proudest observation over the decades: Aurobindo children excel wherever they go, in whatever they choose — because the inner foundation was laid before six.
              </p>
            </div>

            {/* Right: stat cards */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { number: '1200+', label: 'Children graduated' },
                { number: '25+',   label: 'Years of Montessori' },
                { number: '∞',     label: 'Paths taken' },
                { number: '1',     label: 'Common foundation' },
              ].map((s) => (
                <div
                  key={s.label}
                  className="bg-[#EFF6FF] rounded-3xl p-6 text-center border border-blue-100"
                >
                  <p
                    className="text-4xl font-black text-[#1E3A8A] mb-1"
                    style={{ fontFamily: 'Nunito, sans-serif' }}
                  >
                    {s.number}
                  </p>
                  <p className="text-slate-500 text-sm font-medium">{s.label}</p>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* ── ONE STORY WE LOVE TO TELL ── */}
      <section className="py-20 bg-[#F4F7FB]" data-testid="alumni-story-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3 text-center">A story from the school</p>
          <h2
            className="text-2xl sm:text-3xl font-extrabold text-[#1E3A8A] mb-12 text-center"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            One story we love to tell
          </h2>

          {/* ── Aadhya's card ── */}
          <div className="bg-white rounded-3xl shadow-[0_4px_30px_rgba(30,58,138,0.09)] border border-blue-50 overflow-hidden">
            {/* Header band */}
            <div className="bg-[#1E3A8A] px-8 py-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-blue-300 text-xs font-bold uppercase tracking-widest mb-1">Aadhya Vuppala</p>
                  <p className="text-white font-black text-xl" style={{ fontFamily: 'Nunito, sans-serif' }}>
                    Aadhya's journey: 2017–2025
                  </p>
                </div>
                <span className="inline-block bg-white/10 text-white text-xs font-bold px-4 py-2 rounded-full w-fit">
                  8 years at the House of Children
                </span>
              </div>
            </div>

            <div className="p-8">
              {/* Story summary */}
              <p className="text-slate-600 leading-relaxed mb-6">
                Aadhya joined the House of Children as a small child and graduated from our Montessori programme eight years later — and her story says everything about what these years build.
              </p>
              <p className="text-slate-600 leading-relaxed mb-6">
                Alongside a strong academic foundation, she has won Gold, Silver and Bronze medals in gymnastics at district, state and national level, and state championship medals in karate. Her father describes those achievements as "a testament to the values and confidence cultivated during her Montessori years" — the perseverance, focus and courage were formed long before the medals arrived.
              </p>

              {/* Achievement chips */}
              <div className="flex flex-wrap gap-2 mb-6">
                {achievements.map((a) => (
                  <div key={a.label} className="bg-[#EFF6FF] border border-blue-100 rounded-xl px-4 py-2">
                    <p className="text-[#1E3A8A] font-bold text-xs">{a.label}</p>
                    <p className="text-slate-500 text-xs">{a.note}</p>
                  </div>
                ))}
              </div>

              {/* Expand / collapse full testimonial */}
              <button
                onClick={() => setAadhyaExpanded(!aadhyaExpanded)}
                data-testid="aadhya-expand-btn"
                className="inline-flex items-center gap-2 text-[#1E3A8A] font-bold text-sm hover:underline mb-4 focus:outline-none"
              >
                {aadhyaExpanded ? (
                  <>Close story <ChevronUp size={16} /></>
                ) : (
                  <>Read Aadhya's complete story <ChevronDown size={16} /></>
                )}
              </button>

              {aadhyaExpanded && (
                <div className="bg-[#EFF6FF] rounded-2xl px-7 py-6 border-l-4 border-[#1E3A8A] mb-4" data-testid="aadhya-full-story">
                  <Quote size={28} className="text-[#BFDBFE] mb-3" />
                  <p className="text-slate-700 leading-relaxed italic text-base mb-5">
                    "The Montessori environment nurtured her natural curiosity, encouraged independent thinking, instilled self-discipline, and helped her develop into a confident, compassionate, and responsible young individual."
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-[#1E3A8A] rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-bold">A</span>
                    </div>
                    <div>
                      <p className="font-bold text-[#1E3A8A] text-sm">Mr. Aditya</p>
                      <p className="text-slate-500 text-xs">Father of Aadhya Vuppala</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Famous Montessori aside */}
              <div className="bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 mt-2">
                <p className="text-slate-500 text-xs leading-relaxed">
                  <span className="font-semibold text-slate-600">Aadhya is in famous company — </span>
                  Montessori alumni worldwide include Jeff Bezos of Amazon and Google's founders Larry Page and Sergey Brin, who have each credited their Montessori years for their curiosity and independence.
                </p>
              </div>
            </div>
          </div>

          {/* ── Aarav & Pranav card ── */}
          <div className="bg-white rounded-3xl shadow-[0_4px_30px_rgba(30,58,138,0.07)] border border-blue-50 p-8 mt-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-[#DBEAFE] rounded-xl flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-[#1E3A8A] font-black text-sm">AP</span>
              </div>
              <div>
                <p className="font-black text-[#1E3A8A] text-base mb-1" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  Aarav &amp; Pranav
                </p>
                <p className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-3">Brothers · Early years at the House of Children</p>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Brothers Aarav and Pranav spent their early years here. Today, in Grade 9, they top their class with 97% — and it's everything around the marks that makes us proudest: they recite the entire Vishnu Sahasranama by heart and shine in sports and elocution. That is the long arc of a Montessori beginning.
                </p>
              </div>
            </div>
          </div>

          {/* More stories coming placeholder */}
          <div
            className="mt-6 border-2 border-dashed border-blue-200 rounded-3xl px-8 py-6 text-center"
            data-testid="more-stories-placeholder"
          >
            <p className="text-[#60A5FA] font-bold text-sm uppercase tracking-widest mb-1">More stories to follow</p>
            <p className="text-slate-400 text-sm">
              1–3 more alumni stories across generations will be added here. The page launches fine with Aadhya's story and the brothers above.
            </p>
          </div>

        </div>
      </section>

      {/* ── WHAT OUR ALUMNI PROVE ── */}
      <section className="py-20 bg-white" data-testid="alumni-proof-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3 text-center">In the real world</p>
          <h2
            className="text-2xl sm:text-3xl font-extrabold text-[#1E3A8A] mb-4 text-center"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            What our alumni prove
          </h2>
          <p className="text-slate-500 text-center text-base mb-10 max-w-xl mx-auto">
            To a parent asking "but will Montessori work in the real world?" — our alumni are the answer:
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {proofPoints.map((pt, i) => (
              <div
                key={i}
                className="bg-[#EFF6FF] rounded-2xl p-6 flex items-start gap-4 border border-blue-100"
              >
                <div className="w-8 h-8 bg-[#1E3A8A] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle size={14} className="text-white" />
                </div>
                <p className="text-slate-700 leading-relaxed text-sm">{pt.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ARE YOU AN AUROBINDO ALUM? ── */}
      <section className="py-20 bg-[#1E3A8A]" data-testid="alumni-cta-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300 mb-4">Stay connected</p>
          <h2
            className="text-3xl sm:text-4xl font-extrabold text-white mb-5"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            Are you an Aurobindo alum?
          </h2>
          <p className="text-blue-200 text-base leading-relaxed mb-10 max-w-xl mx-auto">
            We would love to hear from you — where life has taken you, and what stayed with you from your years in the House of Children. Your story might be the reassurance a new parent needs today.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={WA_ALUMNI_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="alumni-whatsapp-btn"
              className="bg-[#25D366] text-white rounded-full px-8 py-4 font-black text-sm hover:bg-[#128C7E] transition-all duration-200 hover:-translate-y-0.5 shadow-md inline-flex items-center justify-center gap-2"
            >
              <MessageCircle size={16} /> Share on WhatsApp
            </a>
            <a
              href={EMAIL_ALUMNI}
              data-testid="alumni-email-btn"
              className="bg-white text-[#1E3A8A] rounded-full px-8 py-4 font-black text-sm hover:bg-blue-50 transition-all duration-200 hover:-translate-y-0.5 shadow-md inline-flex items-center justify-center gap-2"
            >
              <Mail size={16} /> Write to us by email
            </a>
          </div>

          <p className="text-blue-300 text-xs mt-8">
            Or write to us at{' '}
            <a href="mailto:sriaurobindo60@gmail.com" className="underline hover:text-white transition-colors">
              sriaurobindo60@gmail.com
            </a>
          </p>
        </div>
      </section>

    </div>
  );
}
